import isEqual from 'lodash/isEqual'
import { useEffect, useState } from 'react'

const ONE_HOUR_MS = 60 * 60 * 1000
const CHECK_INTERVAL_MS = 60 * 1000 // Check every minute

function isEventStartingSoon(eventStartDateTime) {
  if (!eventStartDateTime) return false
  const eventStart = new Date(eventStartDateTime).getTime()
  const oneHourFromNow = Date.now() + ONE_HOUR_MS
  return eventStart < oneHourFromNow
}

export default function useInsurance({ orderItems, formSettings, sessionId }) {
  const { isInsuranceEnabled: isInsuranceEnabledFromServer, insurance, insuranceResetKey, formId } = formSettings
  const { insuranceProvider, eventStartDateTime } = insurance || {}

  const [isStartingSoon, setIsStartingSoon] = useState(() => isEventStartingSoon(eventStartDateTime))

  const teakQuote = useTeakInsurance({ insurance, orderItems, insuranceResetKey, formId, sessionId })

  useEffect(() => {
    setIsStartingSoon(isEventStartingSoon(eventStartDateTime))

    if (!eventStartDateTime) return

    const intervalId = setInterval(() => {
      setIsStartingSoon(isEventStartingSoon(eventStartDateTime))
    }, CHECK_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [eventStartDateTime])

  let insuranceQuote = null
  switch (insuranceProvider) {
    case 'Teak':
      insuranceQuote = teakQuote
      break
  }

  // Disable insurance if event is starting soon, UNLESS user has already opted in
  const userHasOptedIn = insuranceQuote?.isProtected === true
  const isInsuranceEnabled = isInsuranceEnabledFromServer && (!isStartingSoon || userHasOptedIn)

  return { isInsuranceEnabled, insuranceProvider, insuranceQuote }
}

function useTeakInsurance({ insurance, orderItems, insuranceResetKey, formId, sessionId }) {
  const { clientId, apiKey, currency, sandbox } = insurance || {}

  const [prevFormId, setPrevFormId] = useState(formId)
  const [items, setItems] = useState(null)
  const [isTeakConfigured, setIsTeakConfigured] = useState(false)
  const [isComplete, setIsCompleted] = useState(false)
  const [isProtected, setIsProtected] = useState(false)
  const [quoteAmount, setQuoteAmount] = useState(null)
  const [quoteToken, setQuoteToken] = useState(null)
  const [hasInteracted, setHasInteracted] = useState(false)

  const isTeakReady = insurance && typeof window.tg === 'function' && !!document.getElementById('tg-placeholder')

  // Fully reset token on new registration (resetKey), different form (formId change), logout (resetKey)
  useEffect(() => {
    const isNewRegistration = insuranceResetKey !== undefined && insuranceResetKey > 0
    const isDifferentForm = formId && prevFormId && formId !== prevFormId

    if (!isNewRegistration && !isDifferentForm) {
      setPrevFormId(formId)
      return
    }

    // Full reset: clear widget AND persisted selection
    if (typeof window.tg?.clear === 'function') {
      window.tg.clear()
      window.tg.removePersistedSession()
    }

    setIsTeakConfigured(false)
    setIsCompleted(false)
    setIsProtected(false)
    setQuoteAmount(null)
    setQuoteToken(null)
    setItems(null)
    setPrevFormId(formId)
  }, [insuranceResetKey, formId])

  const updateTeakQuote = () => {
    if (!isTeakReady) {
      return
    }

    setIsCompleted(window.tg.isFormComplete())
    setIsProtected(window.tg.isProtected())
    setQuoteAmount(window.tg.get('quote'))
    setQuoteToken(window.tg.get('token'))
  }

  // Configure widget - always clear before configure
  useEffect(() => {
    if (!insurance) {
      return
    }

    if (!isTeakReady) {
      return
    }

    if (!isTeakConfigured) {
      if (typeof window.tg?.clear === 'function') {
        window.tg.clear()
      }

      const items = _getInsuranceItems(orderItems)
      setItems(items)
      const handleUserSelection = () => {
        setHasInteracted(true)
        updateTeakQuote()
      }

      window.tg('configure', {
        apiKey,
        sandbox,
        currency,
        locale: 'en_AU',
        persist: true,
        items,
        referenceNumber: sessionId,
        optInCb: handleUserSelection,
        optOutCb: handleUserSelection,
        updatedCb: updateTeakQuote,
      })

      setIsTeakConfigured(true)
    }
  }, [insurance, isTeakReady, isTeakConfigured])

  useEffect(() => {
    const newItems = _getInsuranceItems(orderItems)

    if (!isEqual(items, newItems)) {
      setItems(newItems)
    }
  }, [orderItems])

  useEffect(() => {
    if (!items || !isTeakReady || !isTeakConfigured) {
      return
    }

    window.tg('update', { items })
  }, [items])

  return {
    clientId,
    isComplete,
    isProtected,
    hasInteracted,
    ...(isComplete && {
      quoteAmount: Number(quoteAmount || 0),
      quoteToken: quoteToken || null,
    }),
  }
}

function _getInsuranceItems(orderItems = []) {
  return orderItems.map(({ totalPrice }) => ({ cost: (totalPrice || 0).toFixed(2) })).filter(i => i.cost > 0)
}
